import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { serverModelToInputModel } from "./converter/server-model-to-input-model";
import { useSnackFormModelMethods } from "./methods";
import { excludeKeys, type DesignSnackFormBaseModel, type ExcludeKeys, type ServerModel } from "./type";
import { snackInputZodSchema } from "./validation-schema";

import type { SnackFormBaseModelMethodsType } from "./methods";
import type { SnackZodSchemaType } from "./validation-schema";

type Props = {
	hotelID: string;
	data: ServerModel;
};

type ViewModel = DesignSnackFormBaseModel["ViewModel"];
type InputModel = DesignSnackFormBaseModel["InputModel"];
type QueryData = DesignSnackFormBaseModel["QueryData"];
export type DesignSnackFormBaseModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export type SnackFormBaseModelType = {
	viewModel: DesignSnackFormBaseModel["ViewModel"];
	resetViewModel: () => void;
	methods: SnackFormBaseModelMethodsType;
};

export const useCreateSnackFormBaseModel = ({ data }: Props): SnackFormBaseModelType => {
	const inputModel = serverModelToInputModel({ snackData: data });

	const initialViewModel = inputModelToViewModelConverter<InputModel, ExcludeKeys, QueryData, SnackZodSchemaType>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: snackInputZodSchema,
	});

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: DesignSnackFormBaseModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				SnackZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: snackInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		viewModel,
		resetViewModel,
		methods: {
			...useSnackFormModelMethods({ onSetViewModel }),
		},
	};
};
